import type { LayoutBlock } from '../content/studio';

export const OPEN_GRAPH_IMAGE_MODE = {
  auto: 'auto',
  custom: 'custom',
} as const;

export type OpenGraphImageMode =
  (typeof OPEN_GRAPH_IMAGE_MODE)[keyof typeof OPEN_GRAPH_IMAGE_MODE];

/** Facebook / Open Graph recommended share card size (≈1.91:1). */
export const OPEN_GRAPH_IMAGE_WIDTH = 1200;
export const OPEN_GRAPH_IMAGE_HEIGHT = 630;
export const OPEN_GRAPH_IMAGE_MIME = 'image/jpeg' as const;

const HERO_COMPONENT_TYPES = new Set([
  'Hero_Editorial_Split',
  'Hero_Video_Atmosphere',
]);

function isHeroBlock(block: LayoutBlock): boolean {
  if (HERO_COMPONENT_TYPES.has(block.componentType)) {
    return true;
  }
  return Boolean(block.registryBlockKey?.startsWith('Hero_'));
}

/**
 * Prefer hero still (`heroImage`), then video poster (`heroPoster`).
 */
export function resolveHeroOpenGraphSource(blocks: readonly LayoutBlock[]): string | null {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const hero = ordered.find(isHeroBlock);
  if (!hero?.media) {
    return null;
  }
  const image = hero.media.heroImage?.trim();
  if (image) {
    return image;
  }
  const poster = hero.media.heroPoster?.trim();
  return poster || null;
}

/** First non-empty image-like media value on the canvas (gallery / dish fallback). */
export function resolveFirstCanvasImageSource(blocks: readonly LayoutBlock[]): string | null {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  for (const block of ordered) {
    for (const [key, raw] of Object.entries(block.media ?? {})) {
      if (/video/i.test(key)) {
        continue;
      }
      const value = raw?.trim();
      if (!value) {
        continue;
      }
      if (
        value.startsWith('/uploads/') ||
        value.startsWith('/platform/defaults/') ||
        /^https?:\/\//i.test(value)
      ) {
        return value;
      }
    }
  }
  return null;
}

export function absolutizeMediaUrlForOpenGraph(
  mediaUrl: string,
  siteOrigin: string,
): string {
  const trimmed = mediaUrl.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const origin = siteOrigin.replace(/\/$/, '');
  if (trimmed.startsWith('/')) {
    return `${origin}${trimmed}`;
  }
  return `${origin}/${trimmed}`;
}

/**
 * Auto-follow share cards go through `/api/og-image` so crawlers always get
 * a standard 1200×630 JPEG (cover-crop), regardless of hero origin size.
 */
export function buildAutoOpenGraphDerivativeUrl(
  sourceMediaUrl: string,
  siteOrigin: string,
): string {
  const origin = siteOrigin.replace(/\/$/, '');
  const src = sourceMediaUrl.trim();
  const params = new URLSearchParams({ src });
  return `${origin}/api/og-image?${params.toString()}`;
}

export type ResolveOpenGraphImageInput = {
  seo: {
    openGraphImage?: string;
    openGraphImageMode?: OpenGraphImageMode;
  };
  pageBlocks: readonly LayoutBlock[];
  /** Used when the active page has no hero still (e.g. Impressum). */
  homepageBlocks?: readonly LayoutBlock[];
  /** Last-resort platform/theme hero path when canvas has no still. */
  platformHeroFallback?: string | null;
  siteOrigin: string;
};

/**
 * Auto mode resolves from hero (then homepage / canvas / platform fallback)
 * and returns a normalized `/api/og-image` derivative URL.
 * Custom mode uses the stored override URL (already normalized on upload).
 */
export function resolveOpenGraphImageUrl(input: ResolveOpenGraphImageInput): string | null {
  const mode = input.seo.openGraphImageMode ?? OPEN_GRAPH_IMAGE_MODE.auto;

  if (mode === OPEN_GRAPH_IMAGE_MODE.custom) {
    const custom = input.seo.openGraphImage?.trim();
    if (!custom) {
      return null;
    }
    return absolutizeMediaUrlForOpenGraph(custom, input.siteOrigin);
  }

  const fromPage =
    resolveHeroOpenGraphSource(input.pageBlocks) ??
    resolveFirstCanvasImageSource(input.pageBlocks);
  const fromHome = input.homepageBlocks
    ? resolveHeroOpenGraphSource(input.homepageBlocks) ??
      resolveFirstCanvasImageSource(input.homepageBlocks)
    : null;
  const raw =
    fromPage ?? fromHome ?? input.platformHeroFallback?.trim() ?? null;
  if (!raw) {
    return null;
  }
  return buildAutoOpenGraphDerivativeUrl(raw, input.siteOrigin);
}
